import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useArtieScore } from "@/hooks/use-artie-score";
import { RecommendationCard } from "@/components/score/RecommendationCard";
import { GoDeeperDialog } from "@/components/score/GoDeeperDialog";
import { CompleteRecommendationDialog } from "@/components/score/CompleteRecommendationDialog";
import { Recommendation } from "@/types/artie-score";
import { Loader2, Check, Minus, ChevronRight, ChevronLeft, Download, Calendar, DollarSign, Users, MapPin, FileText, Award, Shield, AlertCircle, Plus } from "lucide-react";
import { TrafficLightStatus } from "@/components/score/TrafficLightCard";
import { ProductionReadinessForm } from "@/components/ProductionReadinessForm";

export const ProductionReadinessPage = () => {
    const { projectId } = useParams();
    const { scoreData, loading, refresh } = useArtieScore(projectId);
    const [showForm, setShowForm] = useState(false);
    const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
    const [goDeeperOpen, setGoDeeperOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

    const { data: project, refetch: refetchProject } = useQuery({
        queryKey: ["project", projectId],
        queryFn: () => apiClient.getProject(projectId!),
        enabled: !!projectId,
    });

    if (loading) return (
        <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
    );
    
    if (!scoreData) return <div>No data available</div>;

    const readiness = scoreData.production_readiness_analysis;
    const status = (scoreData.production_readiness_status?.toLowerCase() || 'red') as TrafficLightStatus;

    // Helper function to get status icon and color
    const getStatusDisplay = (hasData: boolean) => {
        if (hasData) {
            return { icon: Check, color: "text-green-600", bg: "bg-green-50", label: "🟢" };
        }
        return { icon: Minus, color: "text-red-600", bg: "bg-red-50", label: "🔴" };
    };

    // Production Plan Checklist
    const productionPlan = [
        {
            element: "Timeline",
            status: project?.launch_date ? "green" : "red",
            whatNeeded: "Shoot dates, release dates",
            hasData: !!project?.launch_date,
            data: project?.launch_date ? new Date(project.launch_date).toLocaleDateString() : "Not specified"
        },
        {
            element: "Budget status",
            status: project?.budget_funded_amount ? "green" : "red",
            whatNeeded: (() => {
                if (project?.brand_revenue_target && project?.budget_funded_amount) {
                    const percentage = Math.round((project.budget_funded_amount / project.brand_revenue_target) * 100);
                    const gap = project.brand_revenue_target - project.budget_funded_amount;
                    return `${percentage}% funded, $${gap.toLocaleString()} gap`;
                } else if (project?.budget_funded_amount) {
                    return `$${project.budget_funded_amount.toLocaleString()} funded (total budget not specified)`;
                }
                return "% funded, gap to close";
            })(),
            hasData: !!project?.budget_funded_amount,
            data: project?.budget_funded_amount
                ? `$${project.budget_funded_amount.toLocaleString()} funded${project.brand_revenue_target ? ` of $${project.brand_revenue_target.toLocaleString()}` : ''}`
                : "Not specified"
        },
        {
            element: "Below-the-line team",
            status: project?.team_members && project.team_members.length > 0 ? "green" : "red",
            whatNeeded: "Key roles filled, bios attached",
            hasData: !!(project?.team_members && project.team_members.length > 0),
            data: project?.team_members && project.team_members.length > 0
                ? `${project.team_members.length} team member${project.team_members.length > 1 ? 's' : ''} added`
                : "Team information not provided"
        },
        {
            element: "Logistics",
            status: (project?.locations || project?.equipment_list || project?.permits) ? "green" : "red",
            whatNeeded: "Locations, equipment, permits",
            hasData: !!(project?.locations || project?.equipment_list || project?.permits),
            data: (project?.locations || project?.equipment_list || project?.permits)
                ? `${[project?.locations?.length, project?.equipment_list?.length, project?.permits?.length].filter(Boolean).length} logistics items added`
                : "Logistics details not provided"
        }
    ];

    // Verification Checklist
    const verificationChecklist = [
        {
            element: "Past brand work",
            status: project?.brand_work_portfolio && project.brand_work_portfolio.length > 0 ? "green" : "red",
            whatNeeded: "Contracts, briefs, video links",
            hasData: !!(project?.brand_work_portfolio && project.brand_work_portfolio.length > 0),
            data: project?.brand_work_portfolio && project.brand_work_portfolio.length > 0
                ? `${project.brand_work_portfolio.length} brand work example${project.brand_work_portfolio.length > 1 ? 's' : ''} added`
                : "No brand work portfolio provided"
        },
        {
            element: "Past credits",
            status: project?.production_credits && project.production_credits.length > 0 ? "green" : "red",
            whatNeeded: "Produced work samples",
            hasData: !!(project?.production_credits && project.production_credits.length > 0),
            data: project?.production_credits && project.production_credits.length > 0
                ? `${project.production_credits.length} production credit${project.production_credits.length > 1 ? 's' : ''} added`
                : "No production credits provided"
        },
        {
            element: "Bond/insurance",
            status: project?.insurance_coverage?.has_coverage ? "green" : "red",
            whatNeeded: "Proof of coverage (if applicable)",
            hasData: !!project?.insurance_coverage?.has_coverage,
            data: project?.insurance_coverage?.has_coverage
                ? `${project.insurance_coverage.type || 'Insurance'} coverage documented`
                : "No insurance documentation provided"
        }
    ];

    // Calculate overall readiness percentage
    const allItems = [...productionPlan, ...verificationChecklist];
    const completedItems = allItems.filter(item => item.hasData).length;
    const readinessPercentage = Math.round((completedItems / allItems.length) * 100);

    // Filter recommendations for Production Readiness
    const prodRecommendations = scoreData.recommendations.filter(r => {
        const metric = r.related_metric?.toLowerCase() || '';
        const text = r.what_to_improve?.toLowerCase() || '';
        
        const isProduction = metric.includes('production') || metric.includes('script') || metric.includes('craft');
        const isReach = metric.includes('reach') || metric.includes('audience');
        const isBrand = metric.includes('brand') || metric.includes('safety');

        if (isProduction) return true;

        if (text.includes('production') || text.includes('budget') || text.includes('script') ||
            text.includes('story') || text.includes('character') || text.includes('dialogue') ||
            text.includes('structure') || text.includes('plot') || text.includes('pacing')) return true;

        if (!isReach && !isBrand) return true;

        return false;
    });

    const handleAccept = async (recId: string) => {
        try {
            await apiClient.updateRecommendation(projectId!, {
                recommendation_id: recId,
                status: 'accepted',
            });
            await refresh();
        } catch (error) {
            console.error('Failed to accept recommendation:', error);
        }
    };

    const handleDecline = async (recId: string) => {
        try {
            await apiClient.updateRecommendation(projectId!, {
                recommendation_id: recId,
                status: 'declined',
            });
            await refresh();
        } catch (error) {
            console.error('Failed to decline recommendation:', error);
        }
    };

    const handleGoDeeper = (recId: string) => {
        const rec = prodRecommendations.find(r =>
            (r.id || r.what_to_improve) === recId
        );
        if (rec) {
            setSelectedRecommendation(rec);
            setGoDeeperOpen(true);
        }
    };

    const handleComplete = (recId: string) => {
        const rec = prodRecommendations.find(r =>
            (r.id || r.what_to_improve) === recId
        );
        if (rec) {
            setSelectedRecommendation(rec);
            setCompleteDialogOpen(true);
        }
    };

    return (
        <div className="flex-1 z-10 flex flex-col w-full max-w-6xl pb-20 items-center animate-slide-up mx-auto pt-16 md:pt-0">
            
            {/* Top Navigation */}
            <nav className="w-full px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between z-30">
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                    <Link to={`/projects/${projectId}`} className="md:hidden p-2 -ml-2 text-muted-foreground hover:text-foreground shrink-0">
                        <ChevronLeft className="w-5 h-5" />
                    </Link>
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-ibm text-muted-foreground flex items-center gap-1 whitespace-nowrap overflow-hidden">
                            <Link to="/dashboard" className="hidden sm:inline hover:text-foreground cursor-pointer transition-colors">Dashboard</Link>
                            <ChevronRight className="hidden sm:block w-3 h-3 opacity-50" />
                            <Link to={`/projects/${projectId}`} className="hover:text-foreground cursor-pointer transition-colors truncate max-w-[100px] sm:max-w-[200px]">
                                {project?.title || "Project"}
                            </Link>
                            <ChevronRight className="w-3 h-3 opacity-50 flex-shrink-0" />
                            <span className="text-foreground font-medium truncate">Production Readiness</span>
                        </span>
                    </div>
                    <h2 className="font-cabinet font-bold text-foreground text-lg sm:text-xl tracking-tight truncate">
                        Production Readiness
                    </h2>
                    </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={() => setShowForm(true)}
                        className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Complete Details</span>
                    </button>
                    <button className="px-3 py-1.5 bg-secondary text-foreground rounded-lg text-xs font-medium border border-border hover:bg-border transition-colors flex items-center gap-2">
                        <Download className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Export Report</span>
                    </button>
                </div>
            </nav>

            <main className="w-full px-4 sm:px-6 flex flex-col items-center">
                {/* Info Banner */}
                <div className="w-full mb-6 p-4 bg-blue-50/50 border border-blue-100 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-blue-900 font-semibold mb-1">The Trifecta for Brands</p>
                            <p className="text-xs text-blue-800 leading-relaxed">
                                Brands look for three key elements: <strong>Production timeline</strong> (this is real, not hypothetical), 
                                <strong> Budget top-sheet</strong> (they know what they're funding), and 
                                <strong> Below-the-line team bios + past work</strong> (proof you can execute). 
                                Creators who have all three are significantly more attractive to brand partners.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Overall Status Card */}
                <div className="w-full mb-8 p-6 bg-card border border-border rounded-2xl">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="font-cabinet font-semibold text-lg mb-1">Overall Production Readiness</h3>
                            <p className="text-sm text-muted-foreground">
                                {completedItems} of {allItems.length} elements completed
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-3xl font-cabinet font-bold text-foreground mb-1">
                                {readinessPercentage}%
                            </div>
                            <div className="flex items-center gap-2">
                                {status?.toLowerCase() === 'green' ? (
                                    <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
                                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                        Market Ready
                                    </div>
                                ) : status?.toLowerCase() === 'yellow' ? (
                                    <div className="flex items-center gap-1 text-yellow-600 text-xs font-medium">
                                        <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                                        In Progress
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 text-red-600 text-xs font-medium">
                                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        Early Stage
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-1000 ease-out"
                            style={{ width: `${readinessPercentage}%` }}
                        ></div>
                    </div>
                </div>

                {/* Production Plan Checklist */}
                <div className="w-full mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-cabinet font-semibold text-xl">Production Plan Checklist</h3>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground bg-card px-3 py-1.5 rounded-full border border-border shadow-sm">
                            <FileText className="w-3 h-3" />
                            <span>Logistics Assessment</span>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-secondary/30 border-b border-border">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Element</th>
                                        <th className="text-center px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">What's Needed</th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current State</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {productionPlan.map((item, idx) => {
                                        const statusDisplay = getStatusDisplay(item.hasData);
                                        return (
                                            <tr key={idx} className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {idx === 0 && <Calendar className="w-4 h-4 text-muted-foreground" />}
                                                        {idx === 1 && <DollarSign className="w-4 h-4 text-muted-foreground" />}
                                                        {idx === 2 && <Users className="w-4 h-4 text-muted-foreground" />}
                                                        {idx === 3 && <MapPin className="w-4 h-4 text-muted-foreground" />}
                                                        <span className="text-sm font-medium">{item.element}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-lg">{statusDisplay.label}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-xs text-muted-foreground">{item.whatNeeded}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`text-xs ${item.hasData ? 'text-foreground font-medium' : 'text-muted-foreground italic'}`}>
                                                        {item.data}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Verification Checklist */}
                <div className="w-full mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-cabinet font-semibold text-xl">Verification Checklist</h3>
                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground bg-card px-3 py-1.5 rounded-full border border-border shadow-sm">
                            <Shield className="w-3 h-3" />
                            <span>Proof of Delivery</span>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-secondary/30 border-b border-border">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Element</th>
                                        <th className="text-center px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">What's Needed</th>
                                        <th className="text-left px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current State</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {verificationChecklist.map((item, idx) => {
                                        const statusDisplay = getStatusDisplay(item.hasData);
                                        return (
                                            <tr key={idx} className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {idx === 0 && <FileText className="w-4 h-4 text-muted-foreground" />}
                                                        {idx === 1 && <Award className="w-4 h-4 text-muted-foreground" />}
                                                        {idx === 2 && <Shield className="w-4 h-4 text-muted-foreground" />}
                                                        <span className="text-sm font-medium">{item.element}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-lg">{statusDisplay.label}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-xs text-muted-foreground">{item.whatNeeded}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`text-xs ${item.hasData ? 'text-foreground font-medium' : 'text-muted-foreground italic'}`}>
                                                        {item.data}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Key Gaps */}
                {readiness.gaps.length > 0 && (
                    <div className="w-full">
                        <h3 className="font-cabinet font-semibold text-lg mb-4 flex items-center gap-2">
                            Key Gaps
                            <span className="bg-red-100 text-red-600 text-xs py-0.5 px-2 rounded-full border border-red-200 font-medium">Action Required</span>
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Production Gaps */}
                            {readiness.gaps.map((gap, idx) => (
                                <div key={`gap-${idx}`} className="flex flex-col p-5 bg-card border border-border rounded-xl hover:shadow-md transition-all relative overflow-hidden group">
                                    <div className="absolute top-0 left-0 w-1 h-full bg-red-400"></div>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 rounded-md bg-red-50 text-red-600">
                                                <AlertCircle className="w-4 h-4" />
                                            </div>
                                            <h4 className="text-sm font-semibold font-cabinet">Production Gap</h4>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground bg-secondary px-2 py-1 rounded">
                                            High Priority
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
                                        {gap}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Production Readiness Recommendations */}
                {prodRecommendations.length > 0 && (
                    <div className="w-full mt-8">
                        <h3 className="font-cabinet font-semibold text-lg mb-4 flex items-center gap-2">
                            Production Recommendations
                            <span className="bg-secondary text-muted-foreground text-xs py-0.5 px-2 rounded-full border border-border">{prodRecommendations.length}</span>
                        </h3>

                        <div className="flex flex-col gap-3">
                            {prodRecommendations.map((rec, idx) => (
                                <RecommendationCard
                                    key={idx}
                                    recommendation={rec}
                                    projectId={projectId!}
                                    onAccept={handleAccept}
                                    onDecline={handleDecline}
                                    onGoDeeper={handleGoDeeper}
                                    onComplete={handleComplete}
                                />
                            ))}
                        </div>
                    </div>
                )}

            </main>

            {/* Dialogs */}
            {selectedRecommendation && (
                <>
                    <GoDeeperDialog
                        open={goDeeperOpen}
                        onOpenChange={setGoDeeperOpen}
                        recommendation={selectedRecommendation}
                        projectId={projectId!}
                        onUpdate={refresh}
                    />
                    <CompleteRecommendationDialog
                        open={completeDialogOpen}
                        onOpenChange={setCompleteDialogOpen}
                        recommendation={selectedRecommendation}
                        projectId={projectId!}
                        onComplete={() => {
                            refresh();
                            setCompleteDialogOpen(false);
                        }}
                    />
                </>
            )}

            {/* Production Readiness Form Modal */}
            {showForm && (
                <ProductionReadinessForm
                    projectId={projectId!}
                    onClose={() => setShowForm(false)}
                    onSuccess={() => {
                        setShowForm(false);
                        refetchProject();
                    }}
                    initialData={project}
                />
            )}
        </div>
    );
};